import t from "@/lang/lang";
import { useOfferContext } from "@/routes/processes/[processId]/qpm/offers/[offerId]/_components/offer-context";
import {
  OfferImmutabilityReason,
  SetOfferImmutableRequest,
} from "@/services/backend/qpm/offers/types/offer";
import { useQpmOfferSetImmutableMutation } from "@/services/backend/qpm/offers/api/offer-api";
import { rtkErrIsValidationError } from "@/shared/lib/errors/validation-error";
import { rtkErrIsErrDocumentNotReady } from "@/shared/bsm/core/document/components/ui/err-document-not-ready";
import { useAuth } from "@/shared/lib/authorization/auth-context";
import { Button } from "@/shared/components/ui/button";
import { DialogClose, DialogFooter } from "@/shared/components/ui/dialog";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import { Lock, Unlock } from "lucide-react";
import { useEffect, useMemo, useState } from "react";
import { DialogTooltipButton } from "@/shared/components/dialog/dialog-tooltip-button";

export function SetOfferImmutableButton({
  offerId,
  immutable = false,
  disabled,
  triggerLabel,
}: {
  offerId: string;
  immutable?: boolean;
  disabled?: boolean;
  triggerLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const { user } = useAuth();
  const { setValidationError } = useOfferContext();
  const [setImmutable, { error, isLoading, isSuccess, reset }] =
    useQpmOfferSetImmutableMutation();

  const request: SetOfferImmutableRequest = useMemo(
    () => ({
      id: offerId,
      reason: OfferImmutabilityReason.SetByUser,
      setBy: user?.id ?? "",
    }),
    [offerId, user?.id],
  );

  useEffect(() => {
    if (!(
      rtkErrIsValidationError(error) || rtkErrIsErrDocumentNotReady(error)
    )) {
      return;
    }
    setValidationError(error.data);
  }, [error, setValidationError]);

  useEffect(() => {
    if (!isSuccess) {
      return;
    }
    setOpen(false);
    reset();
  }, [isSuccess, reset]);

  const stateIcon = immutable ? (
    <Lock className="h-5 w-5" />
  ) : (
    <Unlock className="h-5 w-5" />
  );

  return (
    <DialogTooltipButton
      open={open}
      onOpenChange={setOpen}
      tooltip={
        immutable
          ? t("Angebot ist festgeschrieben")
          : t("Angebot festschreiben")
      }
      dialogTitle={t("Angebot festschreiben")}
      triggerContent={
        triggerLabel ? (
          <>
            {stateIcon}
            <span>{triggerLabel}</span>
          </>
        ) : (
          stateIcon
        )
      }
      buttonProps={{
        type: "button",
        variant: "outline",
        disabled: immutable || disabled || isLoading,
        className: triggerLabel ? "w-full justify-start gap-2" : undefined,
      }}
      content={
        <>
          <p className="text-muted-foreground text-sm">
            {t(
              "Mit dem Festschreiben wird das Angebot verbindlich gespeichert und kann danach nicht mehr bearbeitet werden.",
            )}
          </p>
          <p className="text-muted-foreground mt-2 text-sm">
            {t(
              "Für Änderungen erstellen Sie bitte ein neues Angebot. Dafür können Sie ein bestehendes Angebot als Vorlage verwenden.",
            )}
          </p>
          <RTKQueryErrorAlert
            error={error}
            onDismiss={() => {
              reset();
            }}
          />
          <ImmutableValidationHints error={error} />
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">{t("Abbrechen")}</Button>
            </DialogClose>
            <Button
              onClick={() => {
                if (isLoading || immutable) {
                  return;
                }
                setImmutable(request);
              }}
              disabled={isLoading || immutable}
            >
              <Lock className="mr-2 h-4 w-4" />
              {t("Angebot festschreiben")}
            </Button>
          </DialogFooter>
        </>
      }
    />
  );
}

function ImmutableValidationHints({ error }: { error: unknown }) {
  const fieldErrors = rtkErrIsValidationError(error)
    ? error.data.errors
    : rtkErrIsErrDocumentNotReady(error)
      ? error.data.errors
      : null;

  if (!fieldErrors) {
    return null;
  }

  const fieldLabelMap: Record<string, string> = {
    "postalAddress.company": "Postadresse",
    "postalAddress.contact": "Postadresse",
    "postalAddress.street": "Postadresse",
    "postalAddress.postbox": "Postadresse",
    "postalAddress.postalCode": "Postadresse",
    "postalAddress.city": "Postadresse",
    "serviceAddress.company": "Leistungsadresse",
    "serviceAddress.street": "Leistungsadresse",
    "serviceAddress.postalCode": "Leistungsadresse",
    "serviceAddress.city": "Leistungsadresse",
    "representative.name": "Betreuer",
    "representative.email": "Betreuer",
    "representative.mobilephone": "Betreuer",
    date: "Angebotsdatum",
    validUntilDate: "Gültig bis",
    "coverLetterText.coverLetterSalutation": "Anschreiben",
    "offerConfigSnapshot.textBlocks": "Textbausteine",
  };

  const groupedByLabel = Object.entries(fieldErrors).reduce<
    Record<string, string[]>
  >((acc, [field, messages]) => {
    const fieldLabel = fieldLabelMap[field] ?? field;
    const normalizedMessages = messages.map((message) => {
      if (message === "email must be a valid e-mail") {
        return "Bitte geben Sie eine gültige E-Mail-Adresse an.";
      }
      return message;
    });
    const merged = [...(acc[fieldLabel] ?? []), ...normalizedMessages];
    acc[fieldLabel] = Array.from(new Set(merged));
    return acc;
  }, {});

  const entries = Object.entries(groupedByLabel);
  if (entries.length === 0) {
    return null;
  }

  return (
    <div className="border-destructive/30 bg-destructive/5 rounded-md border p-3 text-sm">
      <p className="text-destructive font-medium">
        {t(
          "Das Angebot kann noch nicht festgeschrieben werden. Bitte ergänzen Sie zuerst folgende Angaben:",
        )}
      </p>
      <ul className="text-destructive mt-2 list-disc space-y-1 pl-5">
        {entries.map(([fieldLabel, messages]) => {
          return messages.map((message) => (
            <li key={`${fieldLabel}-${message}`}>
              <span className="font-medium">{fieldLabel}: </span>
              <span>{message}</span>
            </li>
          ));
        })}
      </ul>
    </div>
  );
}
